import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Loader2,
  Building2,
  CheckCircle2,
  FileSignature,
  FolderOpen,
  ExternalLink,
  UploadCloud,
  FileCheck2,
  Trash2,
  StickyNote,
  X,
} from "lucide-react";
import {
  createContract,
  updateContract,
  getContractById,
} from "../../../api/crm/contracts/info";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { AnimatePresence, motion } from "framer-motion";
import { hasPermission } from "../../../utils/permissionUtils";
import { extractFileName } from "../documents/contractDocumentUtils";

const statusOptions = [
  { value: "Đang thực hiện", label: "Đang thực hiện" },
  { value: "Hoàn thành", label: "Hoàn thành" },
  { value: "Chậm tiến độ", label: "Chậm tiến độ" },
  { value: "Sắp hết hạn", label: "Sắp hết hạn" },
];

const getEmptyForm = () => ({
  contract_code: "",
  customer_id: "",
  branch_id: "",
  title: "",
  content: "",
  start_date: new Date().toISOString().split("T")[0],
  end_date: "",
  status: "Đang thực hiện",
  note: "",
  file_unsigned: null,
});

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const serializeForm = (f) =>
  JSON.stringify({
    contract_code: f.contract_code,
    customer_id: f.customer_id,
    branch_id: f.branch_id,
    title: f.title,
    content: f.content,
    start_date: f.start_date,
    end_date: f.end_date,
    status: f.status,
    note: f.note,
  });

const SectionHeader = ({ icon: Icon, tone, title, subtitle }) => (
  <div className="mb-5 flex items-center gap-3">
    <div className={`rounded-2xl p-3 ${tone}`}>
      <Icon size={18} />
    </div>
    <div>
      <h3 className="text-sm font-black uppercase tracking-[0.16em] text-gray-700">{title}</h3>
      <p className="text-xs font-medium text-gray-400">{subtitle}</p>
    </div>
  </div>
);

const fieldLabel =
  "ml-1 block text-[10px] font-black uppercase tracking-[0.18em] text-gray-400";
const inputBase =
  "w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 outline-none transition focus:border-red-400 focus:bg-white";

const ContractCreateModal = ({ isOpen, onClose, editId, onRefresh }) => {
  const navigate = useNavigate();
  const canViewDocuments = hasPermission("crm.documents", "R");

  // Chi nhánh của tài khoản đang đăng nhập (mặc định, không cho chọn).
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const userBranchId = currentUser?.branch?.id || currentUser?.branch_id || "";
  const userBranchName =
    currentUser?.branch?.name || currentUser?.branch_name || currentUser?.branch?.branch_name || "";
  const [loading, setLoading] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState(getEmptyForm());
  const [existingFileUrl, setExistingFileUrl] = useState("");
  const [documentCount, setDocumentCount] = useState(0);

  // Snapshot dữ liệu ban đầu để phát hiện đã có thao tác/nhập liệu hay chưa.
  const initialFormRef = useRef(serializeForm(getEmptyForm()));
  // Ref tới handler huỷ mới nhất + cờ chống mở nhiều confirm cùng lúc.
  const handleCancelRef = useRef(() => {});
  const isCancelingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEsc = (e) => {
      if (e.key === "Escape") handleCancelRef.current();
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    getAllBranches()
      .then((res) => {
        const data = Array.isArray(res?.items) ? res.items : [];
        setBranchOptions(
          data.map((branch) => ({
            label: branch.name || branch.branch_name || "Chi nhánh không tên",
            value: branch.id,
          }))
        );
      })
      .catch((err) => {
        console.error(err);
        setBranchOptions([]);
      });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (!editId) {
      const emptyForm = { ...getEmptyForm(), branch_id: userBranchId };
      setFormData(emptyForm);
      initialFormRef.current = serializeForm(emptyForm);
      setExistingFileUrl("");
      setDocumentCount(0);
      return;
    }

    getContractById(editId)
      .then((res) => {
        if (!res) return;

        const loadedForm = {
          contract_code: res.contract_code || "",
          customer_id: res.customer_id || "",
          branch_id: res.branch_id || "",
          title: res.title || "",
          content: res.content || "",
          start_date: res.start_date?.slice(0, 10) || "",
          end_date: res.end_date?.slice(0, 10) || "",
          status: res.status || "Đang thực hiện",
          note: res.note || "",
          file_unsigned: null,
        };
        setFormData(loadedForm);
        initialFormRef.current = serializeForm(loadedForm);
        setExistingFileUrl(res.file_unsigned || "");
        setDocumentCount(res.document_count || res.documents?.length || 0);
      })
      .catch((err) => {
        console.error(err);
        showErrorToast("Không thể tải dữ liệu hợp đồng");
      });
  }, [isOpen, editId, userBranchId]);

  useEffect(() => {
    if (!formData.branch_id) {
      setCustomers([]);
      return;
    }

    getCustomersByBranch(formData.branch_id)
      .then((res) => {
        setCustomers(Array.isArray(res?.items) ? res.items : []);
      })
      .catch((err) => {
        console.error(err);
        setCustomers([]);
      });
  }, [formData.branch_id]);

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({
        value: c.id,
        label: `${c.name} — ${c.ward || ""}`,
      })),
    [customers]
  );

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
      backgroundColor: "#f9fafb",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const handleChange = (name, value) => {
    setFormData((prev) => {
      if (name === "branch_id") {
        return { ...prev, branch_id: value, customer_id: "" };
      }

      return { ...prev, [name]: value };
    });
  };

  const applyFile = (file) => {
    if (file) setFormData((prev) => ({ ...prev, file_unsigned: file }));
  };

  const handleFileChange = (e) => {
    applyFile(e.target.files?.[0] || null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) applyFile(file);
  };

  const clearSelectedFile = () => {
    setFormData((prev) => ({ ...prev, file_unsigned: null }));
  };

  const isFormDirty = () => {
    if (formData.file_unsigned instanceof File) return true;
    return serializeForm(formData) !== initialFormRef.current;
  };

  const handleCancel = async () => {
    if (loading || isCancelingRef.current) return;

    // Chưa có thao tác nào → đóng thẳng, không hỏi lại.
    if (!isFormDirty()) {
      onClose();
      return;
    }

    isCancelingRef.current = true;
    const confirm = await showConfirmDialog({
      title: "Xác nhận huỷ",
      text: "Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn huỷ thao tác này?",
      confirmText: "Đồng ý",
      cancelText: "Giữ lại",
    });
    isCancelingRef.current = false;

    if (confirm.isConfirmed) {
      onClose();
    }
  };

  handleCancelRef.current = handleCancel;

  const handleSave = async () => {
    const required = ["contract_code", "title", "customer_id", "start_date", "status"];
    for (const field of required) {
      if (!formData[field]) {
        showErrorToast(
          "Vui lòng nhập đầy đủ Mã hợp đồng, Tiêu đề, Khách hàng, Ngày bắt đầu và Trạng thái"
        );
        return;
      }
    }

    if (!formData.file_unsigned && !existingFileUrl) {
      showErrorToast("Vui lòng tải lên file hợp đồng đã ký");
      return;
    }

    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "file_unsigned") {
        if (value instanceof File) payload.append(key, value);
        return;
      }

      if (value !== null && value !== undefined) {
        payload.append(key, value);
      }
    });

    if (editId && !formData.file_unsigned && existingFileUrl) {
      payload.append("file_unsigned", existingFileUrl);
    }

    try {
      setLoading(true);

      if (editId) {
        await updateContract(editId, payload);
      } else {
        await createContract(payload);
      }

      showSuccessToast(editId ? "Cập nhật hợp đồng thành công!" : "Lưu hợp đồng thành công!");
      onRefresh?.();
      onClose();
    } catch (err) {
      console.error("Lỗi tạo/cập nhật hợp đồng:", err);
      showErrorToast("Không thể lưu hợp đồng");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const hasFile = !!formData.file_unsigned || !!existingFileUrl;

  return createPortal(
    <AnimatePresence>
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9998] bg-black/45 backdrop-blur-sm"
          onClick={handleCancel}
        />

        <div className="fixed inset-0 z-[9999] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 320 }}
            className="pointer-events-auto flex h-[90dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
            onClick={(e) => e.stopPropagation()}
          >
          <div className="relative shrink-0 overflow-hidden border-b border-gray-100 bg-gradient-to-r from-red-700 via-red-700 to-red-800 px-5 py-5 text-white sm:px-8 sm:py-7">
            <div className="absolute -right-10 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute bottom-0 right-14 h-24 w-24 rounded-full bg-black/10 blur-2xl" />

            <div className="relative flex items-start gap-3 sm:gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-red-700 shadow-xl shadow-red-950/20 sm:h-16 sm:w-16 sm:rounded-[1.5rem]">
                <FileSignature className="h-6 w-6 sm:h-8 sm:w-8" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-black uppercase tracking-[0.24em] text-red-100">
                  Hợp đồng CRM
                </p>
                <h2 className="mt-1 text-lg font-black tracking-tight sm:mt-1.5 sm:text-2xl">
                  {editId ? "Cập nhật hợp đồng đã ký" : "Tải lên hợp đồng đã ký"}
                </h2>
                <p className="mt-1 text-xs font-medium text-red-100/90 sm:mt-1.5 sm:text-sm">
                  Lưu trữ bản hợp đồng đã ký kết cùng thông tin quản lý.
                </p>
              </div>
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-xl border border-white/20 bg-white/10 p-2 text-white transition hover:bg-white/20"
                aria-label="Đóng"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gray-50/40 p-5 sm:p-6 sm:p-8">
            <div className="space-y-6">
              {/* Hero: Upload file hợp đồng đã ký */}
              <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                <SectionHeader
                  icon={UploadCloud}
                  tone="bg-red-50 text-red-600"
                  title="File hợp đồng đã ký"
                  subtitle="Bản scan/PDF hợp đồng đã ký kết (bắt buộc)"
                />

                <input
                  id="contract-file-unsigned"
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  className="hidden"
                />

                <label
                  htmlFor="contract-file-unsigned"
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  className={`flex cursor-pointer items-center justify-center gap-4 rounded-[1.5rem] border-2 border-dashed px-6 py-8 text-center transition ${
                    isDragging
                      ? "border-red-400 bg-red-50/70"
                      : hasFile
                        ? "border-emerald-200 bg-emerald-50/40"
                        : "border-gray-300 bg-gray-50 hover:border-red-300 hover:bg-red-50/40"
                  }`}
                >
                  {formData.file_unsigned ? (
                    <>
                      <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-600">
                        <FileCheck2 size={26} />
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="truncate text-sm font-bold text-gray-800">
                          {formData.file_unsigned.name}
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-gray-400">
                          {formatFileSize(formData.file_unsigned.size)} · Bấm để đổi file
                        </p>
                      </div>
                    </>
                  ) : existingFileUrl ? (
                    <>
                      <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-600">
                        <FileCheck2 size={26} />
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="truncate text-sm font-bold text-gray-800">
                          {extractFileName(existingFileUrl)}
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-gray-400">
                          Đã có file · Bấm để thay thế
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-2xl bg-red-50 p-3 text-red-500">
                        <UploadCloud size={26} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-700">
                          Kéo thả hoặc bấm để chọn file
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-gray-400">
                          Hỗ trợ PDF, DOCX, JPG, PNG
                        </p>
                      </div>
                    </>
                  )}
                </label>

                <div className="mt-3 flex flex-wrap items-center gap-4">
                  {existingFileUrl && !formData.file_unsigned && (
                    <a
                      href={existingFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:underline"
                    >
                      <ExternalLink size={13} />
                      Xem file hiện có
                    </a>
                  )}
                  {formData.file_unsigned && (
                    <button
                      type="button"
                      onClick={clearSelectedFile}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500 transition hover:text-red-600"
                    >
                      <Trash2 size={13} />
                      Bỏ chọn file
                    </button>
                  )}
                  {editId && canViewDocuments && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`/crm/documents?contract_id=${editId}&upload=1`);
                      }}
                      className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700 transition hover:bg-emerald-100"
                    >
                      <FolderOpen size={12} />
                      {documentCount} hồ sơ liên kết
                    </button>
                  )}
                </div>
              </section>

              {/* Thông tin quản lý: 2 cột */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                  <SectionHeader
                    icon={FileSignature}
                    tone="bg-blue-50 text-blue-600"
                    title="Thông tin hợp đồng"
                    subtitle="Mã số, tiêu đề và thời gian hiệu lực"
                  />

                  <div className="space-y-4">
                    <div>
                      <label className={fieldLabel}>Mã hợp đồng</label>
                      <input
                        type="text"
                        value={formData.contract_code}
                        onChange={(e) => handleChange("contract_code", e.target.value)}
                        className={`mt-1 ${inputBase}`}
                        placeholder="VD: HD-2026-001"
                      />
                    </div>

                    <div>
                      <label className={fieldLabel}>Tiêu đề hợp đồng</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleChange("title", e.target.value)}
                        className={`mt-1 ${inputBase}`}
                        placeholder="Hợp đồng cung cấp dịch vụ đào tạo..."
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={fieldLabel}>Ngày bắt đầu</label>
                        <div className="relative mt-1">
                          <Calendar className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            value={formData.start_date}
                            onChange={(e) => handleChange("start_date", e.target.value)}
                            className={`${inputBase} pl-11`}
                          />
                        </div>
                      </div>

                      <div>
                        <label className={fieldLabel}>Ngày kết thúc</label>
                        <div className="relative mt-1">
                          <Calendar className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            value={formData.end_date}
                            onChange={(e) => handleChange("end_date", e.target.value)}
                            className={`${inputBase} pl-11`}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className={fieldLabel}>Trạng thái</label>
                      <div className="mt-1">
                        <Select
                          options={statusOptions}
                          value={statusOptions.find((o) => o.value === formData.status) || null}
                          onChange={(opt) => handleChange("status", opt?.value || "")}
                          styles={selectStyles}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>

                    <div>
                      <label className={fieldLabel}>Tóm tắt / phạm vi hợp đồng</label>
                      <textarea
                        rows={3}
                        value={formData.content}
                        onChange={(e) => handleChange("content", e.target.value)}
                        className={`mt-1 ${inputBase} font-medium`}
                        placeholder="Tóm tắt phạm vi công việc, giá trị hoặc điều khoản chính..."
                      />
                    </div>
                  </div>
                </section>

                <div className="space-y-6">
                  <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                    <SectionHeader
                      icon={Building2}
                      tone="bg-red-50 text-red-600"
                      title="Đối tác và chi nhánh"
                      subtitle="Chọn phạm vi thực hiện hợp đồng"
                    />

                    <div className="space-y-4">
                      <div>
                        <label className={fieldLabel}>Chi nhánh thực hiện</label>
                        <div className="mt-1 flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-600">
                          <Building2 size={16} className="shrink-0 text-gray-400" />
                          <span className="truncate">
                            {branchOptions.find((b) => b.value === formData.branch_id)?.label ||
                              userBranchName ||
                              "Chưa gán chi nhánh"}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className={fieldLabel}>Khách hàng đối tác</label>
                        <Select
                          options={customerOptions}
                          value={customerOptions.find((o) => o.value === formData.customer_id) || null}
                          onChange={(opt) => handleChange("customer_id", opt?.value || "")}
                          placeholder={
                            formData.branch_id ? "Chọn khách hàng..." : "Chọn chi nhánh trước..."
                          }
                          styles={selectStyles}
                          isClearable
                          isDisabled={!formData.branch_id}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                      </div>
                    </div>
                  </section>

                  <section className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm">
                    <SectionHeader
                      icon={StickyNote}
                      tone="bg-slate-100 text-slate-600"
                      title="Ghi chú nội bộ"
                      subtitle="Ghi chú thêm phục vụ quản lý (tùy chọn)"
                    />

                    <div>
                      <label className={fieldLabel}>Nội dung ghi chú</label>
                      <textarea
                        rows={4}
                        value={formData.note}
                        onChange={(e) => handleChange("note", e.target.value)}
                        className={`mt-1 ${inputBase} font-medium`}
                        placeholder="Ghi chú thêm về trạng thái ký kết hoặc đầu mối xử lý..."
                      />
                    </div>
                  </section>
                </div>
            </div>
          </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-6">
            <p className="hidden text-xs font-medium text-gray-400 sm:block">
              Hợp đồng đã ký sẽ được lưu vào CRM để theo dõi và quản lý hồ sơ liên kết.
            </p>

            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={16} />
                )}
                {loading ? "Đang xử lý..." : editId ? "Lưu thay đổi" : "Lưu hợp đồng"}
              </button>
            </div>
          </div>
          </motion.div>
        </div>
      </>
    </AnimatePresence>,
    document.body
  );
};

export default ContractCreateModal;
